import { Slide } from '@progress/kendo-react-animation';
import { Button } from '@progress/kendo-react-buttons';
import { SvgIcon } from '@progress/kendo-react-common';
import { Dialog } from '@progress/kendo-react-dialogs';
import * as turf from '@turf/turf';
import React, { useEffect } from 'react';
import { v4 } from 'uuid';
import CustomPopover from '../../app/components/layout/CustomPopover';
import { EventBus } from '../../app/contexts/event-bus';
import { useWorkspaceItemContext } from '../../app/contexts/workspace/workspace-item-context';
import { useAppDispatch, useAppSelector } from '../../app/hooks/store-hooks';
import useMap from '../../app/hooks/use-map';
import {
    createDrawingObjectWorkspace,
    drawingObjectSetIdleWorkspace,
    propertiesDrawingObjectWorkspace,
    startDrawDrawingObjectWorkspace
} from '../../app/state/slices/workspace-slice';
import { FPMSyncType } from '../../common/enums';
import {
    CloseOutline,
    DrawingCircleOutline,
    DrawingPolygonOutline,
    DrawingRectangleOutline,
    EditOutline,
    ViewListOutline
} from '../../icons/icons';
import { DrawingObjectDelete } from './DrawingObjectDelete';
import DrawingObjectEditWindow from './DrawingObjectEditWindow';
import DrawingObjectGraphicEdit from './DrawingObjectGraphicEdit';
import DrawingObjectList from './DrawingObjectList';
import useDrawingObjectEditLayer from './hooks/useDrawingObjectEditLayer';
import useDrawingObjectLayer from './hooks/useDrawingObjectLayer';
import {
    DrawingObject,
    DrawingObjectCategory,
    DrawingObjectCenterStyle,
    DrawingObjectCenterUnits,
    DrawingObjectCircleGeometry,
    DrawingObjectCreateInfo,
    DrawingObjectFillStyle,
    DrawingObjectLabelAlignment,
    DrawingObjectLineType,
    DrawingObjectOffsetUnits,
    DrawingObjectPolygonGeometry,
    DrawingObjectRectangleGeometry,
    DrawingObjectType
} from './types';

function DrawingObjectProperties(props: { object: DrawingObject }) {
    return (
        <Slide
            direction={'left'}
            className="h-100 z-2"
            style={{ position: 'absolute', right: 0, top: 0, bottom: 0 }}
            componentChildClassName="h-100"
            transitionExitDuration={200}
            transitionEnterDuration={200}>
            <DrawingObjectEditWindow object={props.object} />
        </Slide>
    );
}

const DrawingObjectTools = () => {
    const [showList, setShowList] = React.useState(false);
    const tutorialActive = !!useAppSelector((x) => x.global.tutorial);
    const context = useWorkspaceItemContext();
    const _drawingObject = useAppSelector((x) => x.workspace.drawingObjects);
    // Needed because store objects are immutable
    // TODO: improve this
    const drawingObject = React.useMemo(() => JSON.parse(JSON.stringify(_drawingObject)) as typeof _drawingObject, [_drawingObject]);
    const state = drawingObject.state;
    const map = useMap();
    const drawingObjectLayer = useDrawingObjectLayer();
    const draw = useDrawingObjectEditLayer();
    const dispatch = useAppDispatch();

    React.useEffect(() => {
        if (map && drawingObjectLayer && draw) {
            return EventBus.on('draw.create', (e) => {
                const object = e.drawObject;
                const drawingObject = newDrawingObject(object);
                dispatch(createDrawingObjectWorkspace({ object: drawingObject }));
                if (tutorialActive) {
                    (document.querySelector('#tutorial-next-button') as HTMLButtonElement | null)?.click();
                }
            }).unsubscribe;
        }
    }, [map, drawingObjectLayer, draw, tutorialActive]);

    // Disable tooltip and context menus during draw
    React.useEffect(() => {
        if (state === 'idle') {
            context.flightLayerFacade.selection.enable();
            context.interactionService.setTooltips('enabled');
            context.interactionService.setContextMenu('enabled');
        } else {
            context.flightLayerFacade.selection.disable();
            context.interactionService.setTooltips('disabled');
            context.interactionService.setContextMenu('disabled');
        }
    }, [context, state]);

    //  When navigating away make sure to set the state to idle, because we don't store mid-edit state of drawing objects anywhere
    useEffect(() => {
        return () => {
            dispatch(drawingObjectSetIdleWorkspace());
        };
    }, []);

    const setDrawingMode = React.useCallback(
        (mode: 'rectangle' | 'circle' | 'polygon' | null) => {
            draw?.setMode(mode);
            if (mode) {
                dispatch(startDrawDrawingObjectWorkspace());
            } else {
                dispatch(drawingObjectSetIdleWorkspace());
            }
        },
        [draw]
    );

    const handleListEdit = React.useCallback(
        (object: DrawingObject) => {
            setShowList(false);
            const bbox = getMapBbox(object);
            map?.fitBounds(bbox, {
                padding: 50,
                maxDuration: 500
            });
            dispatch(propertiesDrawingObjectWorkspace({ object }));
        },
        [map, draw, drawingObjectLayer]
    );

    const isIdle = state === 'idle';
    const isDraw = state === 'draw';
    const isEdit = !isIdle && !isDraw;

    return (
        <div>
            {isIdle && (
                <CustomPopover
                    disableDismiss={tutorialActive}
                    trigger={
                        <Button
                            title="Drawing objects"
                            data-tooltip-bottom
                            style={{
                                width: 27,
                                height: 27,
                                borderRadius: '16px'
                            }}
                            svgIcon={EditOutline}
                            id="drawing-objects-tools-btn"></Button>
                    }>
                    <div className="d-flex flex-column gap-0" style={{ fontSize: 12, width: 140 }}>
                        <div className="clickable clickable-item px-2 py-1" onClick={() => setDrawingMode('circle')}>
                            <SvgIcon icon={DrawingCircleOutline} size="small" className="icon-b-1" />
                            <div>Circle/ellipse</div>
                        </div>
                        <div className="clickable clickable-item px-2 py-1" onClick={() => setDrawingMode('rectangle')}>
                            <SvgIcon icon={DrawingRectangleOutline} size="small" className="icon-b-1" />
                            <div>Rectangle</div>
                        </div>
                        <div
                            className="clickable clickable-item px-2 py-1 border-bottom"
                            id="drawing-objects-tools-polygon"
                            onClick={() => setDrawingMode('polygon')}>
                            <SvgIcon icon={DrawingPolygonOutline} size="small" className="icon-b-1" />
                            <div>Polygon</div>
                        </div>
                        <div
                            className="clickable clickable-item px-2 py-1"
                            onClick={() => {
                                setShowList(true);
                            }}>
                            <SvgIcon icon={ViewListOutline} size="small" className="icon-b-1" />
                            <div>All Objects</div>
                        </div>
                    </div>
                </CustomPopover>
            )}

            {isDraw && (
                <Button
                    style={{
                        width: 27,
                        height: 27,
                        borderRadius: '16px'
                    }}
                    title="Cancel drawing"
                    data-tooltip-bottom
                    onClick={() => setDrawingMode(null)}
                    svgIcon={CloseOutline}
                />
            )}

            {isEdit && (
                <Button
                    style={{
                        width: 27,
                        height: 27,
                        borderRadius: '16px'
                    }}
                    data-tooltip-bottom
                    disabled
                    svgIcon={EditOutline}
                />
            )}

            {showList && (
                <Dialog
                    width={'min(1000px, 100vw)'}
                    height={'min(1000px, 100vh)'}
                    title="Drawing objects"
                    onClose={() => setShowList(false)}>
                    <DrawingObjectList onEdit={handleListEdit} />
                </Dialog>
            )}

            {drawingObject.state === 'edit' && <DrawingObjectGraphicEdit object={drawingObject.object} />}
            {(drawingObject.state === 'properties' || drawingObject.state === 'create') && (
                <DrawingObjectProperties object={drawingObject.object} />
            )}
            {drawingObject.state === 'delete' && <DrawingObjectDelete object={drawingObject.object} />}
        </div>
    );
};

function newDrawingObject(object: DrawingObjectCreateInfo): DrawingObject {
    const geometry:
        | { type: DrawingObjectType.rectangle; geometry: DrawingObjectRectangleGeometry }
        | { type: DrawingObjectType.circle; geometry: DrawingObjectCircleGeometry }
        | { type: DrawingObjectType.polygon; geometry: DrawingObjectPolygonGeometry } =
        object.type === DrawingObjectType.circle
            ? {
                  type: DrawingObjectType.circle,
                  geometry: {
                      centerUnits: DrawingObjectCenterUnits.latLng,
                      centerX: object.center[0],
                      centerY: object.center[1],
                      radiusUnits: DrawingObjectOffsetUnits.miles,
                      radiusX: object.radius[0],
                      radiusY: object.radius[1]
                  }
              }
            : object.type === DrawingObjectType.rectangle
              ? {
                    type: DrawingObjectType.rectangle,
                    geometry: {
                        centerUnits: DrawingObjectCenterUnits.latLng,
                        centerX: object.center[0],
                        centerY: object.center[1],
                        sizeUnits: DrawingObjectOffsetUnits.degrees,
                        width: object.width,
                        height: object.height
                    }
                }
              : {
                    type: DrawingObjectType.polygon,
                    geometry: {
                        polygonCoordinates: object.points
                    }
                };

    return {
        ...geometry,
        id: v4(),
        name: object.name || '',
        isCompany: false,
        label: {
            text: object.label?.text ?? '',
            alignment: DrawingObjectLabelAlignment.center,
            color: 'rgba(0,0,0,1)'
        },
        line: {
            color: object.line?.color ?? 'rgba(0,0,0,0.5)',
            style: DrawingObjectLineType.solid,
            width: 2
        },
        fill: {
            color: object.fill?.color ?? 'rgba(255,0,255,0.5)',
            style: object.fill?.style ?? DrawingObjectFillStyle.solid
        },
        center: {
            color: 'rgba(255,255,255,1)',
            size: 10,
            style: DrawingObjectCenterStyle.plus
        },
        zoom: {
            object: 0,
            point: 0,
            label: 0
        },
        altitude: {
            min: null,
            max: null
        },
        alerts: {
            plane: false,
            route: false
        },
        category: object.category ?? DrawingObjectCategory.viewBased,
        fpmProperties: {
            sync: object.fpmProperties?.sync ?? false,
            FPMSyncType: FPMSyncType.global
        },
        validTime: {
            startDay: null,
            startTime: null,
            endDay: null,
            endTime: null,
            activeDaysOfWeek: 0b1111111,
            greyOutNonActive: false
        }
    };
}

function getMapBbox(object: DrawingObject): [number, number, number, number] {
    if (object.type === DrawingObjectType.polygon) {
        let minLat = 90;
        let minLng = 180;
        let maxLat = -90;
        let maxLng = -180;
        for (const point of object.geometry.polygonCoordinates) {
            if (point[0] < minLng) {
                minLng = point[0];
            }
            if (point[0] > maxLng) {
                maxLng = point[0];
            }
            if (point[1] < minLat) {
                minLat = point[1];
            }
            if (point[1] > maxLat) {
                maxLat = point[1];
            }
        }
        return [minLng, minLat, maxLng, maxLat];
    }
    const center = [object.geometry.centerX, object.geometry.centerY];

    if (object.type === DrawingObjectType.circle) {
        const units = object.geometry.radiusUnits === DrawingObjectOffsetUnits.degrees ? 'degrees' : 'nauticalmiles';

        const halfWidth = object.geometry.radiusX / 2;
        const halfHeight = object.geometry.radiusY / 2;

        const e = turf.destination(center, halfWidth, 90, { units }).geometry.coordinates;
        const w = turf.destination(center, halfWidth, -90, { units }).geometry.coordinates;
        const n = turf.destination(center, halfHeight, 0, { units }).geometry.coordinates;
        const s = turf.destination(center, halfHeight, 180, { units }).geometry.coordinates;
        return [w[0], s[1], e[0], n[1]];
    }

    const halfWidth = object.geometry.width / 2;
    const halfHeight = object.geometry.height / 2;
    if (object.geometry.sizeUnits === DrawingObjectOffsetUnits.degrees) {
        return [center[0] - halfWidth, center[1] - halfHeight, center[0] + halfWidth, center[1] + halfHeight];
    }

    const e = turf.rhumbDestination(center, halfWidth, 90, { units: 'nauticalmiles' }).geometry.coordinates;
    const w = turf.rhumbDestination(center, halfWidth, -90, { units: 'nauticalmiles' }).geometry.coordinates;
    const n = turf.rhumbDestination(center, halfHeight, 0, { units: 'nauticalmiles' }).geometry.coordinates;
    const s = turf.rhumbDestination(center, halfHeight, 180, { units: 'nauticalmiles' }).geometry.coordinates;

    return [w[0], s[1], e[0], n[1]];
}

export default DrawingObjectTools;
